import { useCallback } from 'react';
import {
  RefreshAttachmentUrlsResponseSchema,
  type RefreshAttachmentUrlsResponse,
} from '@playerbase/types';

import { supabase } from '../lib/supabase.js';

// ---------------------------------------------------------------------------
// Discord CDN URL refresh, on demand.
//
// The bot stores bare CDN URLs on feedback_pool because Discord's signed
// query string only lives ~24h. To actually open one in the browser we
// must hit the refresh-attachment-urls edge function, which forwards the
// request to Discord's POST /attachments/refresh-urls under the bot
// token and returns a freshly signed URL.
//
// We cache the freshly signed URL keyed by the bare URL with a short
// TTL: Discord's signature is good for ~24h but a long-idle tab could
// hold a stale URL across refresh boundaries. Ten minutes is conservative
// and keeps repeated chip clicks snappy. The cache lives at module scope
// so it survives across rerenders within the same SPA session.
// ---------------------------------------------------------------------------

const CACHE_TTL_MS = 10 * 60 * 1000;

interface CacheEntry {
  refreshed: string;
  storedAt: number;
}

const cache = new Map<string, CacheEntry>();

// Coalesce concurrent in-flight refreshes for the same URL so two chips
// pointing at the same asset don't fan out into two edge calls.
const inflight = new Map<string, Promise<string>>();

function readCached(bareUrl: string): string | null {
  const entry = cache.get(bareUrl);
  if (entry === undefined) return null;
  if (Date.now() - entry.storedAt > CACHE_TTL_MS) {
    cache.delete(bareUrl);
    return null;
  }
  return entry.refreshed;
}

async function fetchRefreshed(bareUrl: string): Promise<string> {
  const existing = inflight.get(bareUrl);
  if (existing !== undefined) return existing;

  const promise = (async (): Promise<string> => {
    const { data, error } = await supabase.functions.invoke<unknown>(
      'refresh-attachment-urls',
      { body: { urls: [bareUrl] } },
    );
    if (error !== null) {
      throw new Error(`refresh_failed: ${error.message}`);
    }
    const parsed = RefreshAttachmentUrlsResponseSchema.safeParse(data);
    if (!parsed.success) {
      throw new Error('refresh_failed: invalid response');
    }
    const fresh = (parsed.data as RefreshAttachmentUrlsResponse).refreshed[bareUrl];
    if (fresh === undefined) {
      // Caller didn't own the URL, or Discord refused to re-sign it.
      throw new Error('refresh_failed: not_available');
    }
    cache.set(bareUrl, { refreshed: fresh, storedAt: Date.now() });
    return fresh;
  })().finally(() => {
    inflight.delete(bareUrl);
  });

  inflight.set(bareUrl, promise);
  return promise;
}

export interface UseAttachmentRefreshResult {
  // Returns a freshly signed URL, hitting the cache when possible.
  // Throws on RLS denial, network failure, or invalid response so the
  // caller can render an inline error.
  getRefreshed: (bareUrl: string) => Promise<string>;
}

export function useAttachmentRefresh(): UseAttachmentRefreshResult {
  const getRefreshed = useCallback(async (bareUrl: string): Promise<string> => {
    const cached = readCached(bareUrl);
    if (cached !== null) return cached;
    return fetchRefreshed(bareUrl);
  }, []);
  return { getRefreshed };
}

// Test seam. Exported under a leading underscore to discourage app code
// from poking at the cache; the only real consumer is attachment-chip's
// vitest, which clears state between cases.
export function _resetAttachmentRefreshCacheForTests(): void {
  cache.clear();
  inflight.clear();
}
